import React, { useState, useContext, useRef } from 'react';
import { useHistory, Switch, Route, useRouteMatch, useParams, Redirect } from 'react-router-dom';
import { findAndSetMentor } from '../../../../helpers/find_mentor_helper.js';

import Context from '../../../../utils/context.js';
import QuestionnaireStopperV2 from '../../question_components/questionnaire_stopper_v2';
import MentorMatchV2 from '../../question_components/mentor_match_v2/index.jsx';
import ImportantSafetyInformationStickyFooter from '../../../isi/sticky_footer/index.jsx';
import BylvayIsi from '../../../isi/important_safety_information/bylvay/index.jsx';
import RadioPillQuestion from '../../../core/compositions/radio_pill_question';
import CheckboxPillQuestion from '../../../core/compositions/checkbox_pill_question';
import DateOfBirthQuestion from '../../../core/compositions/date_of_birth_question';
import AcknowledgmentQuestion from '../../../core/compositions/acknowledgment_question';

import { QuestionnaireStepProvider } from '../../../../wrappers/question_wrapper/step_context.jsx';
import { useQuestionnaireMatch } from '../../../../wrappers/question_wrapper/use_questionnaire_match.js';
import { useSessionState } from '../../../../wrappers/question_wrapper/use_session_state.js';

import styles from '../../questionnaire.module.css';

const BASE_STEPS = [
  'patient-or-caregiver',
  'age',
  'gender',
  'prescription',
  'symptom-management',
  'considering-a-switch',
  'has-spoken-with-doctor-about-treatment',
  'current-prescription',
  'mentor-conversation-topics',
  'no-medical-advice-disclaimer',
  'monitoring-and-safety-reporting',
];

const buildSteps = (patientOrCaregiver) => {
  const steps = [...BASE_STEPS];
  if (isCaregiverChoice(patientOrCaregiver)) {
    steps.splice(1, 0, 'caring-for');
    steps.splice(steps.indexOf('age') + 1, 0, 'patient-age');
  }
  return steps;
};

const ALGS_MIN_AGE_MONTHS = 12;
const PFIC_MIN_AGE_MONTHS = 3;
const ADULT_MIN_AGE_MONTHS = 18 * 12;

const PATIENT_OR_CAREGIVER_OPTIONS = {
  PATIENT_PFIC: 'A patient with PFIC',
  PATIENT_ALGS: 'A patient with ALGS',
  CAREGIVER_PFIC: 'A caregiver of someone with PFIC',
  CAREGIVER_ALGS: 'A caregiver of someone with ALGS',
};

const isCaregiverChoice = (choice) =>
  choice === PATIENT_OR_CAREGIVER_OPTIONS.CAREGIVER_PFIC || choice === PATIENT_OR_CAREGIVER_OPTIONS.CAREGIVER_ALGS;

const conditionFromPatientOrCaregiver = (choice) => {
  if (!choice) return null;
  if (choice.includes('PFIC')) return 'PFIC';
  if (choice.includes('ALGS')) return 'ALGS';
  return null;
};

// Returns the patient's age in whole months relative to `today` (defaults to "now"). The
// component-level age check uses this to decide whether the patient qualifies for the program.
const ageInMonths = (dateOfBirth, today = new Date()) => {
  const dob = new Date(dateOfBirth);
  let months = (today.getFullYear() - dob.getFullYear()) * 12 + (today.getMonth() - dob.getMonth());
  if (today.getDate() < dob.getDate()) months -= 1;
  return months;
};

// BYLVAY is indicated for patients 12+ months with ALGS and 3+ months with PFIC. Patients outside
// those ranges (or with an unknown condition) are not eligible for the mentor program.
const isPatientAgeEligible = (dateOfBirth, condition) => {
  if (!dateOfBirth || !condition) return false;
  const months = ageInMonths(dateOfBirth);
  if (condition === 'ALGS') return months >= ALGS_MIN_AGE_MONTHS;
  if (condition === 'PFIC') return months >= PFIC_MIN_AGE_MONTHS;
  return false;
};

// The mentor program itself is restricted to adults (18+), so a self-identified patient who is a
// minor is not eligible to participate even if BYLVAY is medically indicated for their age.
const isAdult = (dateOfBirth) => {
  if (!dateOfBirth) return false;
  return ageInMonths(dateOfBirth) >= ADULT_MIN_AGE_MONTHS;
};

const toOptions = (choices) => choices.map((c) => ({ value: c, label: c }));

const toggleInArray = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

export default function BylvayQuestionnaire({}) {
  const context = useContext(Context);
  const history = useHistory();
  const { id } = useParams();
  let { path } = useRouteMatch();
  path = path.replace(`:id`, id);
  const {
    match: mentorMatch,
    setMatch: setMentorMatch,
    update,
    patientPartner,
    setPatientPartner,
    mentorFound,
    setMentorFound,
  } = useQuestionnaireMatch({
    id,
    basePath: path,
    getInitialStepUrl: (m) => `/member/mentor-matches/${m.uuid}/bylvay/patient-or-caregiver`,
    mentorMatchRoute: 'mentor-match',
  });
  const [patientOrCaregiver, setPatientOrCaregiver] = useSessionState('bylvay.patientOrCaregiver', undefined);
  const [caringFor, setCaringFor] = useSessionState('bylvay.caringFor', undefined);
  const [caringForOther, setCaringForOther] = useState('');
  const [mentorConversationTopicsOther, setMentorConversationTopicsOther] = useState('');
  const [currentBirthday, setCurrentBirthday] = useState(
    context.auth.isLoggedIn() ? context.auth.getCurrentUser().date_of_birth : null
  );
  const [patientDateOfBirth, setPatientDateOfBirth] = useSessionState('bylvay.patientDateOfBirth', undefined);
  const [gender, setGender] = useState(() =>
    context.auth.isLoggedIn() ? context.auth.getCurrentUser().gender : undefined
  );
  const [hasHadTreatment, setHasHadTreatment] = useSessionState('bylvay.hasHadTreatment', undefined);
  const [symptomManagement, setSymptomManagement] = useSessionState('bylvay.symptomManagement', undefined);
  const [consideringASwitch, setConsideringASwitch] = useSessionState('bylvay.consideringASwitch', undefined);
  const [hasSpokenWithDoctorAboutTreatment, setHasSpokenWithDoctorAboutTreatment] = useSessionState(
    'bylvay.hasSpokenWithDoctor',
    undefined
  );
  const [scheduledTreatment, setScheduledTreatment] = useSessionState('bylvay.scheduledTreatment', undefined);
  const [mentorConversationTopics, setMentorConversationTopics] = useSessionState(
    'bylvay.mentorConversationTopics',
    []
  );
  const [noMedicalAdviceDisclaimer, setNoMedicalAdviceDisclaimer] = useSessionState(
    'bylvay.noMedicalAdvice',
    undefined
  );
  const [monitoringAndSafetyReporting, setMonitoringAndSafetyReporting] = useSessionState(
    'bylvay.monitoringAndSafety',
    undefined
  );
  const isiRef = useRef(null);

  return (
    <QuestionnaireStepProvider steps={buildSteps(patientOrCaregiver)}>
      <Switch>
        <Route exact path={`${path}`}>
          <Redirect to={`${path}/patient-or-caregiver`} />
        </Route>

        {/* 1 */}

        <Route exact path={`${path}/patient-or-caregiver`}>
          <RadioPillQuestion
            heading='This program is designed for patients or caregivers affected by progressive familial intrahepatic cholestasis (PFIC) or Alagille syndrome (ALGS). Which best describes you?'
            name='patient_or_caregiver'
            options={toOptions([
              'A patient with PFIC',
              'A patient with ALGS',
              'A caregiver of someone with PFIC',
              'A caregiver of someone with ALGS',
              'None of the above',
            ])}
            value={patientOrCaregiver}
            onChange={setPatientOrCaregiver}
            onSubmit={() => {
              const answer = patientOrCaregiver;
              const condition = answer.includes('PFIC') ? 'PFIC' : answer.includes('ALGS') ? 'ALGS' : null;
              const treatment = condition;
              const lowerAnswer = answer.toLowerCase();
              const patient_or_caregiver = lowerAnswer.includes('caregiver')
                ? 'Caregiver'
                : lowerAnswer.includes('patient')
                  ? 'Patient'
                  : null;
              update(
                {
                  treatment: treatment,
                  condition: condition,
                  data: { patient_or_caregiver: patient_or_caregiver },
                },
                () => {
                  if (answer === 'None of the above') {
                    history.push(`${path}/thank-you`);
                  } else if (
                    answer === 'A caregiver of someone with PFIC' ||
                    answer === 'A caregiver of someone with ALGS'
                  ) {
                    history.push(`${path}/caring-for`);
                  } else {
                    history.push(`${path}/age`);
                  }
                }
              );
            }}
          />
        </Route>

        {/* 2 — Other input, legacy kept */}

        <Route exact path={`${path}/caring-for`}>
          <RadioPillQuestion
            heading='Who are you caring for?'
            name='caring_for'
            options={toOptions(['A child', 'An adult family member'])}
            value={caringFor}
            onChange={setCaringFor}
            showOther
            otherValue={caringForOther}
            onOtherChange={setCaringForOther}
            otherPlaceholder={'Other (specify)'}
            onSubmit={() => {
              const answer = caringFor || (caringForOther && caringForOther.trim());
              setCaringFor(answer);
              update({ data: { caring_for: answer } }, () => {
                history.push(`${path}/age`);
              });
            }}
            onBack={() => {
              history.push(`${path}/patient-or-caregiver`);
            }}
          />
        </Route>

        {/* 3 — date input, legacy kept */}

        <Route exact path={`${path}/age`}>
          <DateOfBirthQuestion
            heading='What is your date of birth?'
            value={currentBirthday}
            onChange={setCurrentBirthday}
            onSubmit={() => {
              context.auth.userUpdate({ date_of_birth: currentBirthday }, () => {
                if (!isAdult(currentBirthday)) {
                  history.push(`${path}/thank-you`);
                  return;
                }

                if (isCaregiverChoice(patientOrCaregiver)) {
                  history.push(`${path}/patient-age`);
                  return;
                }

                const condition = conditionFromPatientOrCaregiver(patientOrCaregiver);
                if (isPatientAgeEligible(currentBirthday, condition)) {
                  history.push(`${path}/gender`);
                } else {
                  history.push(`${path}/thank-you`);
                }
              });
            }}
            onBack={() => {
              history.push(
                isCaregiverChoice(patientOrCaregiver) ? `${path}/caring-for` : `${path}/patient-or-caregiver`
              );
            }}
          />
        </Route>

        {/* 4 — date input, legacy kept */}

        <Route exact path={`${path}/patient-age`}>
          <DateOfBirthQuestion
            heading="If you are a caregiver, what is the progressive familial intrahepatic cholestasis (PFIC) or Alagille syndrome (ALGS) patient's date of birth?"
            value={patientDateOfBirth}
            onChange={setPatientDateOfBirth}
            onSubmit={() => {
              update({ patient_dob: patientDateOfBirth }, () => {
                const condition = conditionFromPatientOrCaregiver(patientOrCaregiver);
                if (isPatientAgeEligible(patientDateOfBirth, condition)) {
                  history.push(`${path}/gender`);
                } else {
                  history.push(`${path}/thank-you`);
                }
              });
            }}
            onBack={() => {
              history.push(`${path}/age`);
            }}
          />
        </Route>

        {/* 5 */}

        <Route exact path={`${path}/gender`}>
          <RadioPillQuestion
            heading='How would you describe your gender?'
            name='gender'
            options={toOptions(['Male', 'Female', 'Non-binary', 'Prefer not to answer'])}
            value={gender}
            onChange={setGender}
            onSubmit={() => {
              context.auth.userUpdate({ gender }, () => {
                history.push(`${path}/prescription`);
              });
            }}
            onBack={() => {
              history.push(isCaregiverChoice(patientOrCaregiver) ? `${path}/patient-age` : `${path}/age`);
            }}
          />
        </Route>

        {/* 6 — ISI */}

        <Route exact path={`${path}/prescription`}>
          <RadioPillQuestion
            heading='Are you (or someone you care for) currently taking BYLVAY (odevixibat)?'
            name='has_had_treatment'
            options={toOptions(['Yes', 'No', 'Not sure'])}
            value={hasHadTreatment}
            onChange={setHasHadTreatment}
            onSubmit={() => {
              update({ data: { has_had_treatment: hasHadTreatment } }, () => {
                if (hasHadTreatment === 'Yes') {
                  history.push(`${path}/symptom-management`);
                } else {
                  history.push(`${path}/considering-a-switch`);
                }
              });
            }}
            onBack={() => {
              history.push(`${path}/gender`);
            }}
          />

          <ImportantSafetyInformationStickyFooter
            component={<BylvayIsi showFooter />}
            initialHeight={350}
            initialHeightMobile={200}
            expandedHeight={800}
            ref={isiRef}
          />
        </Route>

        {/* 7 — ISI */}

        <Route exact path={`${path}/symptom-management`}>
          <RadioPillQuestion
            heading='Please specify how long you (or someone you care for) have been taking BYLVAY (odevixibat).'
            name='symptom_management'
            options={toOptions([
              'Less than 1 month',
              '1-3 months',
              '4-6 months',
              '7 months to a year',
              'More than 1 year',
            ])}
            value={symptomManagement}
            onChange={setSymptomManagement}
            onSubmit={() => {
              update({ data: { symptom_management: symptomManagement } }, () => {
                history.push(`${path}/mentor-conversation-topics`);
              });
            }}
            onBack={() => {
              history.push(`${path}/prescription`);
            }}
          />

          <ImportantSafetyInformationStickyFooter
            initialHeight={300}
            initialHeightMobile={200}
            expandedHeight={800}
            component={<BylvayIsi />}
            ref={isiRef}
          />
        </Route>

        {/* 8 — ISI */}

        <Route exact path={`${path}/considering-a-switch`}>
          <RadioPillQuestion
            heading='Are you (or someone you care for) considering starting BYLVAY (odevixibat) for itch associated with PFIC or ALGS?'
            name='considering_a_switch'
            options={toOptions(['Yes', 'No', 'Maybe'])}
            value={consideringASwitch}
            onChange={setConsideringASwitch}
            onSubmit={() => {
              update({ data: { considering_a_switch: consideringASwitch } }, () => {
                history.push(`${path}/has-spoken-with-doctor-about-treatment`);
              });
            }}
            onBack={() => {
              history.push(`${path}/prescription`);
            }}
          />

          <ImportantSafetyInformationStickyFooter
            initialHeight={350}
            initialHeightMobile={200}
            expandedHeight={800}
            component={<BylvayIsi />}
            ref={isiRef}
          />
        </Route>

        {/* 9 — ISI */}

        <Route exact path={`${path}/has-spoken-with-doctor-about-treatment`}>
          <RadioPillQuestion
            heading='Have you spoken with your doctor about BYLVAY (odevixibat)?'
            name='has_spoken_with_doctor_about_treatment'
            options={toOptions(['Yes', 'No'])}
            value={hasSpokenWithDoctorAboutTreatment}
            onChange={setHasSpokenWithDoctorAboutTreatment}
            onSubmit={() => {
              update(
                {
                  data: {
                    has_spoken_with_doctor_about_treatment: hasSpokenWithDoctorAboutTreatment,
                  },
                },
                () => {
                  history.push(`${path}/current-prescription`);
                }
              );
            }}
            onBack={() => {
              history.push(`${path}/considering-a-switch`);
            }}
          />

          <ImportantSafetyInformationStickyFooter
            initialHeight={350}
            initialHeightMobile={200}
            expandedHeight={800}
            component={<BylvayIsi />}
            ref={isiRef}
          />
        </Route>

        {/* 10 — ISI */}

        <Route exact path={`${path}/current-prescription`}>
          <RadioPillQuestion
            heading='Do you have a prescription for BYLVAY (odevixibat)?'
            name='scheduled_treatment'
            options={toOptions(['Yes', 'No'])}
            value={scheduledTreatment}
            onChange={setScheduledTreatment}
            onSubmit={() => {
              update({ data: { scheduled_treatment: scheduledTreatment } }, () => {
                history.push(`${path}/mentor-conversation-topics`);
              });
            }}
            onBack={() => {
              history.push(`${path}/has-spoken-with-doctor-about-treatment`);
            }}
          />

          <ImportantSafetyInformationStickyFooter
            initialHeight={350}
            initialHeightMobile={200}
            expandedHeight={800}
            component={<BylvayIsi />}
            ref={isiRef}
          />
        </Route>

        {/* 11 — Other input, legacy kept */}

        <Route exact path={`${path}/mentor-conversation-topics`}>
          <CheckboxPillQuestion
            heading='What are you hoping to get out of connecting with a mentor? (Select all that apply)'
            subHeading='Mentors share personal experiences only and cannot provide medical advice or treatment guidance.'
            name='mentor_conversation_topics'
            options={toOptions([
              "Hearing someone's personal experience living with PFIC or ALGS",
              'Understanding what day-to-day life looks like for others',
              'Feeling less alone',
              'Caregiver-to-caregiver perspective',
              'Learning how others approached conversations with their doctor',
              'Tips that helped others stay organized or navigate daily routines',
            ])}
            values={mentorConversationTopics}
            onToggle={(v) => setMentorConversationTopics((prev) => toggleInArray(prev, v))}
            showOther
            otherValue={mentorConversationTopicsOther}
            onOtherChange={setMentorConversationTopicsOther}
            otherPlaceholder={"Other topics you'd like to discuss (optional)"}
            onSubmit={(values, otherValue) => {
              const result = [...(values || []), ...(otherValue && otherValue.trim() ? [otherValue.trim()] : [])];
              setMentorConversationTopics(result);
              update({ conversation_topics: result }, () => {
                history.push(`${path}/no-medical-advice-disclaimer`);
              });
            }}
            onBack={() => {
              if (hasHadTreatment === 'Yes') {
                history.push(`${path}/symptom-management`);
              } else {
                history.push(`${path}/current-prescription`);
              }
            }}
          />
        </Route>

        {/* 12 */}

        <Route exact path={`${path}/no-medical-advice-disclaimer`}>
          <AcknowledgmentQuestion
            heading='No medical advice'
            body={
              <>
                Mentors are not medical professionals. They cannot provide medical advice or discuss specific side
                effects. I understand that conversations are personal experience only.
              </>
            }
            checked={Boolean(noMedicalAdviceDisclaimer)}
            onCheckedChange={(next) => setNoMedicalAdviceDisclaimer(next ? 'Yes' : undefined)}
            onSubmit={() => {
              update(
                {
                  data: {
                    no_medical_advice_disclaimer: 'Yes',
                    no_medical_advice_disclaimer_datetime: new Date(),
                  },
                },
                () => {
                  history.push(`${path}/monitoring-and-safety-reporting`);
                }
              );
            }}
            onBack={() => {
              history.push(`${path}/mentor-conversation-topics`);
            }}
          />
        </Route>

        {/* 13 */}

        <Route exact path={`${path}/monitoring-and-safety-reporting`}>
          <AcknowledgmentQuestion
            heading='Privacy & safety monitoring'
            body={
              <>
                <p style={{ margin: 0 }}>
                  PatientPartner conducts limited monitoring of all conversations to help ensure patient well-being.
                  This review does not reveal your identity and is handled with strict privacy safeguards.
                </p>
                <p style={{ margin: '16px 0 0' }}>
                  PatientPartner is responsible for identifying and reporting on side effects as they learn of them to
                  share with Ipsen. All of the information is kept confidential. The information collected is used by
                  Ipsen to continue to monitor the safety of Ipsen's marketed products.
                </p>
                <p style={{ margin: '16px 0 0' }}>I acknowledge and understand this.</p>
              </>
            }
            checked={Boolean(monitoringAndSafetyReporting)}
            onCheckedChange={(next) => setMonitoringAndSafetyReporting(next ? 'Yes' : undefined)}
            onSubmit={() => {
              update(
                {
                  data: {
                    monitoring_and_safety_reporting: 'Yes',
                    monitoring_and_safety_reporting_datetime: new Date(),
                  },
                },
                () => {
                  history.push(`${path}/mentor-match`);
                }
              );
              findAndSetMentor(
                {
                  onSetPatientPartner: setPatientPartner,
                  onMentorFound: setMentorFound,
                  onNoMentorFound: () => history.push(`${path}/no-mentor-available`),
                },
                context
              );
            }}
            onBack={() => {
              history.push(`${path}/no-medical-advice-disclaimer`);
            }}
          />
        </Route>

        {/* 14 */}

        <Route exact path={`${path}/thank-you`}>
          <QuestionnaireStopperV2
            detailed
            heading='Thank you for your interest in the BYLVAY (odevixibat) Mentor Program.'
            body={
              <>
                <p>
                  We understand how important it can be to connect with others who have shared experiences. At this
                  time, the BYLVAY Mentor Program is available{' '}
                  <strong>only to adults (18 years of age and older)</strong> who are patients or caregivers affected by
                  itch associated with progressive familial intrahepatic cholestasis (PFIC) or Alagille syndrome (ALGS)
                  and who meet the program's eligibility criteria.
                </p>
                <br />
                <p>BYLVAY (odevixibat) is a prescription medicine for the treatment of itching in:</p>
                <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '6px 0' }}>
                  <li>Patients 12 months of age and older with Alagille syndrome (ALGS)</li>
                  <li>Patients 3 months of age and older with progressive familial intrahepatic cholestasis (PFIC)</li>
                </ul>
                <br />
                <p>
                  <strong>Limitation of Use:</strong>
                  <br />
                  BYLVAY is not recommended for some PFIC type 2 patients who have specific <em>ABCB11</em> gene
                  changes. Talk with your healthcare provider if you have questions.
                </p>
                <br />
                <p>
                  Participation in this program is limited to adults. Based on your responses, you are not eligible to
                  participate in this program at this time.
                </p>
                <br />
                <p>
                  If you have any questions or would like more information, please reach out to us at{' '}
                  <strong>
                    <a
                      href='mailto:info@patientpartner.com'
                      style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                    >
                      info@patientpartner.com
                    </a>
                  </strong>
                  . You may also wish to speak with your healthcare provider or visit{' '}
                  <strong>
                    <a
                      href='https://www.bylvay.com'
                      target='_blank'
                      rel='noreferrer'
                      style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                    >
                      BYLVAY.com
                    </a>
                  </strong>{' '}
                  for additional information.
                </p>
                <br />
                <p style={{ color: '#314d69', fontWeight: 'bold', textTransform: 'uppercase' }}>
                  IMPORTANT SAFETY INFORMATION
                </p>
                <br />
                <p style={{ color: '#314d69' }}>
                  <strong>What are the possible side effects of BYLVAY?</strong>
                  <br />
                  <strong>BYLVAY can cause serious side effects, including:</strong>
                </p>
                <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '6px 0' }}>
                  <li>
                    You should not use BYLVAY if you have had or have an advanced stage of liver disease. Talk with your
                    healthcare provider if you have any liver conditions.
                  </li>
                  <li>
                    Abnormal liver tests have been observed with use of BYLVAY. Liver tests should be obtained before
                    starting and periodically during therapy to monitor for liver injury. Immediately report to your
                    healthcare provider any signs or symptoms of liver injury, such as, nausea, vomiting, skin or the
                    whites of eyes turn yellow, dark or brown urine, pain on the right side of the abdomen, or loss of
                    appetite.
                  </li>
                  <li>Notify your healthcare provider if you experience new onset or worsening of diarrhea.</li>
                  <li>
                    BYLVAY may decrease the absorption of certain vitamins, which include vitamins A, D, E, and K (FSV
                    or fat-soluble vitamins). Your blood levels of these vitamins should be measured before starting and
                    periodically during treatment to assess for FSV deficiency. You may bleed more easily, may bleed
                    longer, or experience a bone fracture. Call your healthcare provider if you have any signs or
                    symptoms of bleeding.
                  </li>
                </ul>
                <br />
                <p>
                  <strong>The most common side effects of BYLVAY include:</strong>
                </p>
                <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '6px 0' }}>
                  <li>
                    The most common side effects of BYLVAY treatment in patients with ALGS are diarrhea, abdominal pain,
                    abnormal bleeding, such as bruising, and decreased weight.
                  </li>
                  <li>
                    The most common side effects of BYLVAY in patients with PFIC are diarrhea, liver test abnormalities,
                    vomiting, abdominal pain, and FSV deficiency.
                  </li>
                  <li>
                    Do not swallow the 200 mcg or 600 mcg capsule(s) containing Oral Pellets whole. These are intended
                    to be opened and the contents mixed into soft food.
                  </li>
                  <li>
                    For patients taking bile acid binding resins, take BYLVAY at least 4 hours before or 4 hours after
                    taking a bile acid binding resin.
                  </li>
                  <li>
                    There is a pregnancy safety study that collects outcome data in women taking BYLVAY during
                    pregnancy. Pregnant women exposed to BYLVAY should report if they have used BYLVAY during pregnancy
                    by calling 1-855-463-5127 and contact your doctor immediately.
                  </li>
                </ul>
                <br />
                <p>
                  <strong>
                    You are encouraged to report side effects to FDA at{' '}
                    <a href='tel:18003321088' style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}>
                      1-800-FDA-1088
                    </a>{' '}
                    or at{' '}
                    <a
                      href='https://www.fda.gov/medwatch'
                      target='_blank'
                      rel='noreferrer'
                      style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                    >
                      www.fda.gov/medwatch
                    </a>
                    . You may also report side effects to Ipsen Biopharmaceuticals, Inc. at 1-855-463-5127.
                  </strong>
                </p>
                <br />
                <p>
                  <strong>
                    Please see full{' '}
                    <a
                      href='https://d2rkmuse97gwnh.cloudfront.net/a88aa6d6-3ca0-4362-a711-d53c45ae33ff/5f7d23c0-f85b-4f9a-907a-3f2fa18aa260/5f7d23c0-f85b-4f9a-907a-3f2fa18aa260_source__v.pdf'
                      target='_blank'
                      rel='noreferrer'
                      style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                    >
                      Prescribing Information
                    </a>{' '}
                    and{' '}
                    <a
                      href='https://d2rkmuse97gwnh.cloudfront.net/a88aa6d6-3ca0-4362-a711-d53c45ae33ff/654d0431-ec8d-45f0-ba80-8a33c9df68ab/654d0431-ec8d-45f0-ba80-8a33c9df68ab_source__v.pdf'
                      target='_blank'
                      rel='noreferrer'
                      style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                    >
                      Instructions for Use
                    </a>{' '}
                    for BYLVAY.
                  </strong>
                </p>
              </>
            }
          />
        </Route>

        {/* 15 */}

        <Route exact path={`${path}/no-mentor-available`}>
          <QuestionnaireStopperV2
            detailed
            heading='Thank you for your interest in the BYLVAY (odevixibat) Mentor Program.'
            body={
              <>
                <p>
                  We appreciate you taking the time to share your experience. Unfortunately, we don't currently have a
                  mentor available who matches your specific journey — but we're actively working to expand our mentor
                  community.
                </p>
                <br />
                <p>
                  We'll keep your information on file and reach out as soon as a mentor who shares your experience
                  becomes available. In the meantime, if you have questions or would like more information, please reach
                  out to us at{' '}
                  <strong>
                    <a
                      href='mailto:support@patientpartner.com'
                      style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                    >
                      support@patientpartner.com
                    </a>
                  </strong>
                  . You may also wish to speak with your healthcare provider or visit{' '}
                  <strong>
                    <a
                      href='https://www.bylvay.com'
                      target='_blank'
                      rel='noreferrer'
                      style={{ fontSize: 'inherit', fontFamily: 'inherit', color: '#314d69' }}
                    >
                      BYLVAY.com
                    </a>
                  </strong>{' '}
                  for additional information.
                </p>
              </>
            }
          />
        </Route>

        <Route exact path={`${path}/mentor-match`}>
          <MentorMatchV2
            patientPartner={patientPartner}
            mentorFound={mentorFound}
            mentorMatch={mentorMatch}
            setMentorMatch={setMentorMatch}
            sessionStoragePrefix='bylvay.'
            onNoMentorFound={() => history.push(`${path}/no-mentor-available`)}
          />
        </Route>
      </Switch>
    </QuestionnaireStepProvider>
  );
}
